import {
  AlertCircle,
  Award,
  Briefcase,
  ClipboardCheck,
  FileCheck2,
  FileSearch,
  FileText,
  ReceiptText,
  ShieldCheck,
  Sparkles,
  Users,
  type LucideIcon,
} from "lucide-react";

export interface AbilityMeta {
  slug: string;
  path: string;
  label: string;
  subtitle: string;
  icon: LucideIcon;
  module: string;
  requiresUpload?: boolean;
  hidden?: boolean;
  pending?: boolean;
}
const UPLOAD_SLUGS = new Set<string>([
  "company-profile",
  "contract-extract",
  "qualification-extract",
  "pre-bid-review",
  "bid-file-review",
  "tender-compliance",
  "direct-award-review",
  "award-extract",
  "contract-consistency",
  "contract-compliance",
  "invoice-ocr",
  "personnel-consistency",
]);

export const ABILITIES: ReadonlyArray<AbilityMeta> = [
  {
    slug: "company-profile",
    path: "/abilities/company-profile",
    label: "建档信息提取",
    subtitle: "供应商/中介机构营业执照字段抽取",
    icon: Briefcase,
    module: "供应商档案",
  },
  {
    slug: "qualification-extract",
    path: "/abilities/qualification-extract",
    label: "资质证书信息提取",
    subtitle: "证书类型、等级、编号、持证主体、有效期",
    icon: ShieldCheck,
    module: "供应商档案",
  },
  {
    slug: "company-risk",
    path: "/abilities/company-risk",
    label: "企业风险信息查询",
    subtitle: "企业工商、司法、经营风险与黑名单核查",
    icon: Users,
    module: "供应商档案",
  },
  {
    slug: "pre-bid-review",
    path: "/abilities/pre-bid-review",
    label: "投标前材料可疑信号检测",
    subtitle: "跨投标人挖嫌疑关联,投标递交前的早期预警",
    icon: AlertCircle,
    module: "评标定标",
  },
  {
    slug: "bid-file-review",
    path: "/abilities/bid-file-review",
    label: "招投标文件审查报告",
    subtitle: "围串标、报价、签章、材料一致性多维审查",
    icon: Sparkles,
    module: "评标定标",
  },
  {
    slug: "award-extract",
    path: "/abilities/award-extract",
    label: "中标结果与评标信息提取",
    subtitle: "多份中标/评标材料自动分类并抽取字段",
    icon: Award,
    module: "评标定标",
  },
  {
    slug: "tender-compliance",
    path: "/abilities/tender-compliance",
    label: "招标文件合规审查",
    subtitle: "限制竞争、资质门槛、评审条款等风险",
    icon: FileSearch,
    module: "招标审批",
  },
  {
    slug: "direct-award-review",
    path: "/abilities/direct-award-review",
    label: "直接委托定标审查",
    subtitle: "多材料审查、报价合理性、方案匹配与证据报告",
    icon: ClipboardCheck,
    module: "招标审批",
  },
  {
    slug: "contract-extract",
    path: "/abilities/contract-extract",
    label: "合同信息提取",
    subtitle: "合同名称、双方、金额、税率与付款计划",
    icon: FileText,
    module: "合同",
  },
  {
    slug: "contract-consistency",
    path: "/abilities/contract-consistency",
    label: "合同一致性校对",
    subtitle: "电子版合同与盖章扫描件逐项比对",
    icon: FileCheck2,
    module: "合同",
  },
  {
    slug: "personnel-consistency",
    path: "/abilities/personnel-consistency",
    label: "人员一致性校验",
    subtitle: "合同人员与投标承诺人员按姓名/证件号比对",
    icon: Users,
    module: "合同",
  },
  {
    slug: "contract-compliance",
    path: "/abilities/contract-compliance",
    label: "合同合规性审查",
    subtitle: "合同条款合规 + 与招投标承诺一致性核查",
    icon: FileSearch,
    module: "合同",
  },
  {
    slug: "invoice-ocr",
    path: "/abilities/invoice-ocr",
    label: "发票识别",
    subtitle: "发票图片/PDF 抽取照面字段与货物明细(含数电票)",
    icon: ReceiptText,
    module: "合同",
  },
  {
    slug: "supplier-relationship",
    path: "/abilities/supplier-relationship",
    label: "供应商关联关系查询",
    subtitle: "多家供应商共同实控人/共享股东/关联控股，输出关系图",
    icon: Users,
    module: "供应商档案",
  },
];

(ABILITIES as Array<AbilityMeta>).forEach((ability) => {
  if (UPLOAD_SLUGS.has(ability.slug)) ability.requiresUpload = true;
});

const ABILITY_MODULES = [
  "供应商档案",
  "招标审批",
  "评标定标",
  "合同",
] as const;

export function abilitiesByModule(): Array<{ module: string; items: AbilityMeta[] }> {
  return ABILITY_MODULES.map((module) => ({
    module,
    items: ABILITIES.filter((ability) => ability.module === module),
  })).filter((group) => group.items.length > 0);
}

const USE_CASE_TO_ABILITY_SLUG: Record<string, string> = Object.fromEntries(
  ABILITIES.map((ability) => [ability.slug, ability.slug]),
);

const ABILITY_BY_SLUG = new Map(ABILITIES.map((ability) => [ability.slug, ability]));

export function getAbilityByUseCaseId(useCaseId: string): AbilityMeta | undefined {
  const abilitySlug = USE_CASE_TO_ABILITY_SLUG[useCaseId];
  return abilitySlug ? ABILITY_BY_SLUG.get(abilitySlug) : undefined;
}

export function resolveAbilityHashAlias(rawHash: string): string | null {
  const normalized = rawHash.replace(/^#/, "").replace(/^\//, "").trim().toLowerCase();
  if (!normalized) return null;
  if (normalized.startsWith("abilities/")) return `/${normalized}`;

  const direct = ABILITY_BY_SLUG.get(normalized);
  return direct?.path ?? null;
}
